import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { TokenInterface } from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM, RouteKey, Template } from '@store/constants'
import { ProjectIdentity } from '@store/types'

import { getQueryStringParams } from '@utilities/helper'

import useGetShowcaseRoomsConfig from '@hooks/useGetShowcaseRoomConfig'

interface Props {
  projectIdentity: ProjectIdentity
  showcaseRoom: string
  token: TokenInterface
}

function useTemplateHandler({
  projectIdentity,
  showcaseRoom,
  token,
}: Props): Template | null {
  const location = useLocation()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const {
    isLoaded,
    isError,
    showcaseRoomConfig: showcaseRoomConfigApiData,
  } = useGetShowcaseRoomsConfig({
    projectName: projectIdentity.projectName,
    room: showcaseRoom || DEFAULT_SHOWCASE_ROOM,
    token,
  })

  const templateFromQuery = useMemo(
    () => getQueryStringParams(location.search)?.template,
    [location.search]
  )

  const routeKey = location.pathname.split('/').pop()
  const isValidRouteKey = Object.values(RouteKey).includes(routeKey)

  useEffect(() => {
    if (isLoaded && showcaseRoomConfigApiData) {
      setIsLoading(false)
    }
    if (isLoaded && isError) {
      setIsLoading(false)
      setHasError(true)
    }
  }, [isLoaded, isError, showcaseRoomConfigApiData, dispatch])

  if (isLoading) {
    return null
  }

  if (!isLoading && hasError) {
    return Template.Default
  }

  const showcaseTemplate = isValidRouteKey
    ? showcaseRoomConfigApiData?.templates?.[routeKey]?.template ||
      Template.Default
    : Template.Default

  return templateFromQuery || showcaseTemplate
}

export default useTemplateHandler
